import { useEffect, useState } from 'react';
import { CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { listPayload, objectPayload } from '@/lib/payload';
import { formatCurrencyAmount } from '@/lib/formatMoney';
import { completeMonths, latestCompleteMonth } from '@/lib/reportPeriods';
import { sortStatusRows } from '@/lib/chartPalette';
import { getApiErrorMessage } from '@/stores/partnerAuthStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatCard } from '@/components/ui/stat-card';
import { StatusPieChart } from '@/components/charts/StatusPieChart';
import { MonthlyBarChart } from '@/components/charts/MonthlyBarChart';
import { RecurringRevenueTable, type MrrRow } from '@/components/revenue/RecurringRevenueTable';
import { MrrByTierTable, type MrrByTierRow } from '@/components/revenue/MrrByTierTable';
import { ChurnPanel, type ChurnRow } from '@/components/revenue/ChurnPanel';
import { MrrMovementChart, type MrrMovementRow } from '@/components/revenue/MrrMovementChart';

interface RevenueRow {
  month: string;
  byCurrency: Record<string, number>;
}

interface ArpuRow {
  currency: string;
  revenue: number;
  paidGroupCount: number;
  arpu: number | null;
}

export function RevenuePage() {
  const [revenue, setRevenue] = useState<RevenueRow[]>([]);
  const [paymentStatus, setPaymentStatus] = useState<Array<{ status: string; count: number }>>([]);
  const [invoiceStatus, setInvoiceStatus] = useState<Array<{ status: string; count: number }>>([]);
  const [outstanding, setOutstanding] = useState<{ byCurrency: Array<{ currency: string; total: number; count: number }> } | null>(null);
  const [arpu, setArpu] = useState<ArpuRow[]>([]);
  const [mrr, setMrr] = useState<MrrRow[]>([]);
  const [mrrByTier, setMrrByTier] = useState<MrrByTierRow[]>([]);
  const [churn, setChurn] = useState<ChurnRow[]>([]);
  const [movement, setMovement] = useState<MrrMovementRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // One toast id for the whole page so a shared failure (session, network)
    // raises one toast rather than five identical ones.
    const reportFailed = (error: unknown) =>
      toast.error(getApiErrorMessage(error), { id: 'revenue-report-error' });

    void api
      .get('/reports/revenue/over-time?months=12')
      .then((res) => setRevenue(listPayload(res)))
      .catch((error) => {
        setRevenue([]);
        reportFailed(error);
      })
      .finally(() => setIsLoading(false));
    void api.get('/reports/revenue/payment-status-mix').then((res) => setPaymentStatus(listPayload(res))).catch((error) => { setPaymentStatus([]); reportFailed(error); });
    void api.get('/reports/revenue/invoice-status-mix').then((res) => setInvoiceStatus(listPayload(res))).catch((error) => { setInvoiceStatus([]); reportFailed(error); });
    void api.get('/reports/revenue/outstanding').then((res) => setOutstanding(objectPayload(res))).catch((error) => { setOutstanding(null); reportFailed(error); });
    void api.get('/reports/revenue/arpu?months=1').then((res) => setArpu(listPayload(res))).catch((error) => { setArpu([]); reportFailed(error); });
    void api.get('/reports/revenue/mrr').then((res) => setMrr(listPayload(res))).catch((error) => { setMrr([]); reportFailed(error); });
    void api.get('/reports/revenue/mrr-by-tier').then((res) => setMrrByTier(listPayload(res))).catch((error) => { setMrrByTier([]); reportFailed(error); });
    void api.get('/reports/subscriptions/churn-rate?months=12').then((res) => setChurn(listPayload(res))).catch((error) => { setChurn([]); reportFailed(error); });
    void api.get('/reports/revenue/mrr-movement?months=12').then((res) => setMovement(listPayload(res))).catch((error) => { setMovement([]); reportFailed(error); });
  }, []);

  /**
   * Currency order is fixed by total revenue across the window, descending.
   * The API's groupBy has no ORDER BY and each month's byCurrency map carries
   * whatever key order the database produced, so an insertion-ordered series
   * would hand the same currency a different identity hue between loads.
   * Sorting within one currency's own totals never compares across them.
   */
  const currencyTotals = revenue.reduce<Record<string, number>>((totals, row) => {
    for (const [currency, amount] of Object.entries(row.byCurrency)) {
      totals[currency] = (totals[currency] ?? 0) + amount;
    }
    return totals;
  }, {});
  const currencies = Object.keys(currencyTotals).sort(
    (a, b) => currencyTotals[b] - currencyTotals[a] || a.localeCompare(b),
  );
  const revenueChartData = revenue.map((row) => ({
    month: row.month,
    ...Object.fromEntries(currencies.map((currency) => [currency, row.byCurrency[currency] ?? 0])),
  }));

  /**
   * The last COMPLETE month's total per currency. The series always ends with
   * the current, still-accumulating month, so headlining it would understate
   * revenue by however much of the month is left, and annualising it would
   * understate the run rate by the same factor.
   *
   * Currencies are never summed: this surface aggregates across groups that
   * bill in different currencies, so a combined figure would be meaningless.
   */
  const latestRow = latestCompleteMonth(revenue);
  const latestByCurrency = latestRow
    ? Object.entries(latestRow.byCurrency)
        .map(([currency, total]) => ({ currency, total }))
        .sort((a, b) => b.total - a.total || a.currency.localeCompare(b.currency))
    : [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold">Revenue</h1>
        <p className="text-muted-foreground">Billing performance across all currencies.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {latestByCurrency.map((row, index) => (
          <StatCard
            key={row.currency}
            title={`Revenue last full month (${row.currency})`}
            value={formatCurrencyAmount(row.total, row.currency)}
            hint={`Annualised ${formatCurrencyAmount(row.total * 12, row.currency)}`}
            icon={CreditCard}
            iconColor={index === 0 ? 'accent' : 'secondary'}
            isLoading={isLoading}
          />
        ))}
        {!isLoading && latestByCurrency.length === 0 && (
          <StatCard title="Revenue last full month" value="None" icon={CreditCard} />
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recurring-revenue metrics. See
            docs/superpowers/specs/2026-08-12-partners-billing-metrics-design.md.
            Everything else on this page reports cash collected; these four
            report contracted recurring revenue, which cannot be derived from it.
            Recurring revenue (MRR/ARR), MRR by plan, Churn and MRR movement all
            have their endpoints now. */}
        <Card>
          <CardHeader>
            <CardTitle>Recurring revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <RecurringRevenueTable rows={mrr} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Churn</CardTitle>
          </CardHeader>
          <CardContent>
            {/* The last COMPLETE month. The series always ends on the current,
                still-accumulating one, whose churn is only churn so far and
                would read as a sudden improvement every time the month rolls. */}
            <ChurnPanel row={latestCompleteMonth(churn)} />
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Revenue over time (by currency)</CardTitle>
          </CardHeader>
          <CardContent>
            <MonthlyBarChart
              data={revenueChartData}
              series={currencies.map((currency) => ({ key: currency, label: currency }))}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>MRR by plan</CardTitle>
          </CardHeader>
          <CardContent>
            <MrrByTierTable rows={mrrByTier} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>MRR movement</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Drops the current, still-accumulating month for the same
                reason as the Churn panel above: it is only partial by
                construction and would plot as a false collapse. */}
            <MrrMovementChart rows={completeMonths(movement)} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Payment status</CardTitle>
          </CardHeader>
          <CardContent>
            <StatusPieChart data={sortStatusRows(paymentStatus, 'status')} nameKey="status" valueKey="count" variant="status" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Invoice status</CardTitle>
          </CardHeader>
          <CardContent>
            <StatusPieChart data={sortStatusRows(invoiceStatus, 'status')} nameKey="status" valueKey="count" variant="status" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Outstanding receivables</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1 text-sm">
              {outstanding?.byCurrency.map((row) => (
                <p key={row.currency}>
                  {row.currency}: {formatCurrencyAmount(row.total, row.currency)} ({row.count} invoices)
                </p>
              ))}
              {!outstanding?.byCurrency.length && <p className="text-muted-foreground">No outstanding invoices.</p>}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>ARPU (this month)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1 text-sm">
              {arpu.map((row) => (
                <p key={row.currency}>
                  {row.currency}: {row.arpu !== null ? formatCurrencyAmount(row.arpu, row.currency) : 'N/A'} across {row.paidGroupCount} paid groups
                </p>
              ))}
              {arpu.length === 0 && <p className="text-muted-foreground">No revenue this period.</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
